import {
  Subject, catchError, debounceTime, of, tap,
} from 'rxjs';
import {
  IMaterialInfo, IMaterialSolutionSubmission, IMaterialStatus, ISolveMaterialDTO,
} from '../../../api/private/learn-engine-api/learn-engine-api.interface';
import { LearnEngineApi } from '../../../api/public/learn-engine/learn-engine-api';
import {
  Container, LearningExperienceType, ModalService, Service, StorageService,
} from '../../../symphony';
import {
  ClickType, aIChatPlaygroundContext, aIChatPlaygroundErrorModalId, maxInputLength,
} from '../aIChatPlaygroundConstants';
import {
  ConversationRole, IPrompt, Tab, MessagePartType, TextMessagePart, ErrorStatusCodes, Message,
} from '../aIChatPlaygroundInterface';
import { AIChatPlaygroundStore } from '../AIChatPlaygroundStore';
import { SlStorageKeys } from '../../../shared/public/globalInterfaces/globalInterfaces';
import { SlLEContext } from '../../../learnEngine/private/global-constants';
import { SlLessonService } from '../../../learnEngine/private/services/Business/sl-lesson.service';
import { AIChatPlaygrountTrackingService } from './AIChatPlaygrountTrackingService';
import { DynamicPaywallLoader } from '../../../_paywalls/public/DynamicPaywallLoader';
import { Touchpoint } from '../../../api/public/subscription-api';

@Service()
export class AIChatPlaygroundService {
  private store = Container.take(aIChatPlaygroundContext.value, AIChatPlaygroundStore);

  private learnEngineApi = Container.take('global', LearnEngineApi);

  private materialInfo: IMaterialInfo;

  private paywallLoader = DynamicPaywallLoader.ensureRegistered();

  private storageService = Container.take('global', StorageService);

  private modalService = Container.take('global', ModalService);

  private lessonService = Container.take(SlLEContext, SlLessonService);

  private trackingService = Container.take(aIChatPlaygroundContext.value, AIChatPlaygrountTrackingService);

  private solveMaterialCallback: (
    statusChanges: IMaterialStatus[],
    solutions: IMaterialSolutionSubmission[],
    materialInfo?: IMaterialInfo,
  ) => void;

  private settingsUpdateTrackSubject = new Subject();

  constructor() {
    this.trackSettingsUpdate();
  }

  private trackSettingsUpdate = () => {
    this.settingsUpdateTrackSubject.pipe(
      debounceTime(2000),
    ).subscribe(() => {
      this.trackingService.trackPlaygroundSettingUpdate();
    });
  };

  public setActiveTab = (activeTab: Tab) => {
    this.trackingService.trackPlaygroundClick(
      activeTab === Tab.Chat ? ClickType.AIChatTab : ClickType.SettingsTab,
    );
    this.store.configs.next({
      ...this.store.configs.value,
      selectedTab: activeTab,
    });
  };

  public newMessageHandler = (message: string, id?: string) => {
    this.store.conversation.next([...this.store.conversation.value, {
      role: ConversationRole.User,
      parts: [{
        type: MessagePartType.Text,
        content: {
          text: message,
        },
      }],
    }]);

    this.getAIResponse(message, id);
  };

  public initialPromptClickHandler = (prompt: IPrompt) => {
    this.trackingService.trackPlaygroundClick(ClickType.SuggestedPrompt);
    this.newMessageHandler(prompt.prompt, prompt.id);
  };

  private messageResponseErrorHandler = (error: any) => {
    this.store.isResponseLoading.next(false);
    this.store.isRegenerating.next(false);
    if (error?.[0]?.code === ErrorStatusCodes.NotOwned) {
      this.paywallLoader.openPaywall({
        touchpoint: Touchpoint.aiChatPlayground,
        props: {}
      });
      this.resetConversation();
    } else {
      this.store.errorStatusCode.next(error?.[0]?.code || ErrorStatusCodes.InternalServerError);
      this.trackingService.trackPlaygroundWarning(error?.[0]?.code);
      if (error?.[0]?.code !== ErrorStatusCodes.ChatGptError) {
        this.modalService.open(aIChatPlaygroundErrorModalId);
      }
    }
    return of(null);
  };

  private regenerateResponseSuccessHandler = (message: Message) => {
    this.store.isResponseLoading.next(false);
    this.store.isRegenerating.next(false);

    this.store.conversation.next([
      ...this.store.conversation.value,
      message,
    ]);
    return of(null);
  };

  private messageResponseSuccessHandler = (data: ISolveMaterialDTO) => {
    this.solveMaterialCallback(data.statusChanges, data.solutions);
    this.store.isResponseLoading.next(false);
    this.store.conversation.next([
      ...this.store.conversation.value,
      data.solutions[0].aiChatResponse.messages[0],
    ]);
    this.store.conversationId.next(data.solutions[0].aiChatResponse.conversationId);

    return of(null);
  };

  private getAIResponse = (message: string, id?: string) => {
    this.store.errorStatusCode.next(null);
    this.store.isResponseLoading.next(true);
    this.store.isInitialState.next(false);
    const session: string = this.storageService.load(
      SlStorageKeys.Sl_LE_SESSION,
      'session',
    );
    const solution = {
      typeId: this.materialInfo.header.typeId,
      materialRelationId: this.materialInfo.header.materialRelationId,
      chat: {
        conversationId: this.store.conversationId.value,
        promptId: id,
        message,
        configs: this.store.modelConfigs.value.map(({ key, value }) => ({ key, value })),
      },
    };

    if (message.length > maxInputLength && !id) {
      this.messageResponseErrorHandler([{ code: ErrorStatusCodes.PromptTooLong }]);
      return;
    }

    if (!this.store.isRegenerating.value) {
      this.learnEngineApi.solveMaterial({
        solutions: [solution],
        experience_type_id: LearningExperienceType.Course,
      }, session).pipe(
        tap(this.messageResponseSuccessHandler),
        catchError(this.messageResponseErrorHandler),
      ).subscribe();
    } else {
      this.learnEngineApi.regenerateAIChatPlaygroundLastMessage(
        this.store.materialHeader.value.materialRelationId,
        this.store.conversationId.value,
        this.store.modelConfigs.value,
        session,
      ).pipe(
        tap(this.regenerateResponseSuccessHandler),
        catchError(this.messageResponseErrorHandler),
      ).subscribe();
    }
  };

  public resetConversation = () => {
    this.store.conversation.next(this.store.initialConversation.value);
    this.store.isInitialState.next(true);
    this.store.conversationId.next(null);
  };

  public initHandler = (data: IMaterialInfo, solveMaterialCallback: (
    statusChanges: IMaterialStatus[],
    solutions: IMaterialSolutionSubmission[],
    materialInfo?: IMaterialInfo,
  ) => void) => {
    this.materialInfo = data;
    this.solveMaterialCallback = solveMaterialCallback;

    if (this.store.isInitialized.value) {
      return;
    }
    this.store.isInitialized.next(true);
    this.store.conversation.next(data.chat.conversation?.messages || []);
    this.store.initialConversation.next(data.chat.conversation?.messages || []);
    this.store.conversationId.next(data.chat.conversation?.conversationId);
    this.store.configs.next(data.chat.configs);
    this.store.modelConfigs.next(data.chat.modelConfigs);
    this.store.initialPrompts.next(data.chat.prompts);
    this.store.materialHeader.next(data.header);
  };

  private retryLastMessage = () => {
    const parts = this.store.conversation.value[this.store.conversation.value.length - 1].parts;
    const message = (parts?.[0] as TextMessagePart)?.content?.text;
    this.getAIResponse(message);
  };

  public retryErrorMessage = () => {
    this.retryLastMessage();
    this.trackingService.trackPlaygroundClick(ClickType.TryAgain);
  };

  public updateSettings = (key: string, value: string) => {
    this.store.modelConfigs.next(this.store.modelConfigs.value.map(
      (config) => (
        config.key === key ? { ...config, value } : config),
    ));
    this.settingsUpdateTrackSubject.next(null);
  };

  public errorCtaCloseHandler = () => {
    const errorStatusCode = this.store.errorStatusCode.value;
    if (!errorStatusCode || errorStatusCode === ErrorStatusCodes.ChatGptError) return;

    this.modalService.close(aIChatPlaygroundErrorModalId);

    if (errorStatusCode === ErrorStatusCodes.PromptTooLong) {
      this.trackingService.trackPlaygroundClick(ClickType.TryAgain);
    }

    if (errorStatusCode === ErrorStatusCodes.LimitReached) {
      this.lessonService.toNextPage();
    }
  };

  public regenerateMessage = () => {
    this.trackingService.trackPlaygroundClick(ClickType.Regenerate);
    this.store.conversation.next(this.store.conversation.value.slice(0, -1));
    this.store.isRegenerating.next(true);
    this.retryLastMessage();
  };
}
